import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import navigationAtom from './navigation.atom'
import { INavigationState } from './navigation.types'
import sandboxAtom from '../Sandbox/sandbox.atom'
import { ITask, ITaskGroup } from '../../types/tasks.types'
import produce from 'immer'

const useNavigationActions = () => {
  const sandboxState = useRecoilValue(sandboxAtom)
  const setSandboxState = useSetRecoilState(sandboxAtom)

  const [navigationState, setNavigationState] = useRecoilState(navigationAtom)

  const toRocket = (rocket: INavigationState['rocket']) => {
    const firstStar = sandboxState.groups.find((g) => g.set === rocket)
    const firstTask = sandboxState.tasks.find((t) => t.group === firstStar.id)

    setNavigationState({ star: firstStar.id, level: firstTask.id, rocket })
  }

  const toStar = (star: INavigationState['star']) => {
    const firstTask = sandboxState.tasks.find((t) => t.group === star)

    setNavigationState((prev) => ({ ...prev, star, level: firstTask.id }))
  }

  const toLevel = (level: INavigationState['level']) => {
    setNavigationState((prev) => ({ ...prev, level }))
  }

  const next = () => {
    const groupTasks = sandboxState.tasks.filter((t) => t.group === navigationState.star)
    const taskIndex = groupTasks.findIndex((t) => t.id === navigationState.level)
    if (taskIndex < groupTasks.length - 1) return toLevel(groupTasks[taskIndex + 1].id)

    const setGroups = sandboxState.groups.filter((g) => g.set === navigationState.rocket)
    const groupIndex = setGroups.findIndex((g) => g.id === navigationState.star)
    if (groupIndex < setGroups.length - 1) return toStar(setGroups[groupIndex + 1].id)

    const setIndex = sandboxState.sets.findIndex((s) => s.id === navigationState.rocket)
    if (setIndex < sandboxState.sets.length - 1) return toRocket(sandboxState.sets[setIndex + 1].id)
  }

  const prev = () => {
    const groupTasks = sandboxState.tasks.filter((t) => t.group === navigationState.star)
    const taskIndex = groupTasks.findIndex((t) => t.id === navigationState.level)
    if (taskIndex > 0) return toLevel(groupTasks[taskIndex - 1].id)

    const setGroups = sandboxState.groups.filter((g) => g.set === navigationState.star)
    const groupIndex = setGroups.findIndex((g) => g.id === navigationState.star)
    if (groupIndex > 0) return toStar(setGroups[groupIndex - 1].id)

    const setIndex = sandboxState.sets.findIndex((s) => s.id === navigationState.rocket)
    if (setIndex > 0) return toRocket(sandboxState.sets[setIndex - 1].id)
  }

  const updatesGroupsOrdering = (updated: ITaskGroup[]) => {
    setSandboxState((prev) =>
      produce(prev, (draft) => {
        for (const g of updated) {
          const index = draft.groups.findIndex((gr) => gr.id === g.id)
          draft.groups[index].orderingIndex = g.orderingIndex
        }

        draft.groups = draft.groups.sort((a, b) => a.orderingIndex - b.orderingIndex)
        return draft
      })
    )
  }

  const updatesTasksOrdering = (updated: ITask[]) => {
    setSandboxState((prev) =>
      produce(prev, (draft) => {
        for (const t of updated) {
          const index = draft.tasks.findIndex((task) => task.id === t.id)
          draft.tasks[index].orderingIndex = t.orderingIndex
        }

        draft.tasks = draft.tasks.sort((a, b) => a.orderingIndex - b.orderingIndex)
        return draft
      })
    )
  }

  return { toLevel, toStar, toRocket, next, prev, updatesGroupsOrdering, updatesTasksOrdering }
}

export default useNavigationActions
